import { z } from "zod";

import { DELETE_CONFIRM_PHRASE } from "./account-deletion";
import { EMAIL_OTP_TYPE_VALUES } from "./email-otp-types";

// Mirrors supabase/config.toml minimum_password_length / password_requirements.
export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_POLICY_HINT =
  `At least ${PASSWORD_MIN_LENGTH} characters with upper, lower, and a digit.`;

// The first-arg `error` covers Conform's coercion of empty FormData values
// to undefined — without it, a blank submit renders the raw zod typeerror
// "expected string, received undefined" instead of friendly copy.
export const emailSchema = z
  .string({ error: "Email is required." })
  .trim()
  .min(1, "Email is required.")
  .email("That doesn't look like a valid email.");

export const passwordSchema = z
  .string({ error: "Password is required." })
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`)
  .regex(/[a-z]/, "Password must include a lowercase letter.")
  .regex(/[A-Z]/, "Password must include an uppercase letter.")
  .regex(/[0-9]/, "Password must include a digit.");

export const signupSchema = z
  .object({
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z
      .string({ error: "Please confirm your password." })
      .min(1, "Please confirm your password."),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
    when: (payload) => payload.issues.every((i) => i.path?.[0] !== "password"),
  });

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string({ error: "Password is required." }).min(1, "Password is required."),
});

export const resetSchema = z.object({
  email: emailSchema,
});

export const updatePasswordSchema = z.object({
  password: passwordSchema,
});

const DISPLAY_NAME_MAX_LENGTH = 60;

/** Click-to-confirm POST body shape for the email-OTP interstitial (#683).
 *  `type` is narrowed to `EmailOtpType` by `z.enum(EMAIL_OTP_TYPE_VALUES)` —
 *  parsing returns the correctly-typed union, replacing both the runtime
 *  `EMAIL_OTP_TYPES.has(type as EmailOtpType)` membership check AND the
 *  follow-up `type as EmailOtpType` cast at the verifyOtp call. */
export const emailOtpConfirmSchema = z.object({
  token_hash: z.string().min(1),
  type: z.enum(EMAIL_OTP_TYPE_VALUES),
  next: z.string().optional(),
});

export const profileUpdateSchema = z.object({
  displayName: z
    .string({ error: "Full name is required." })
    .trim()
    .min(1, "Full name is required.")
    .max(
      DISPLAY_NAME_MAX_LENGTH,
      `Full name must be ${DISPLAY_NAME_MAX_LENGTH} characters or fewer.`,
    ),
});

/** Typed confirmation for account deletion — irreversible and cascades across
 *  every table the user owns, so a single button press must not be enough.
 *  Case-sensitive on purpose: autocorrect capitalising a lowercase entry
 *  should not be what confirms an unrecoverable action. */
export const accountDeleteSchema = z.object({
  // `.trim()` BEFORE the literal: iOS keyboards routinely append a space after
  // an autocorrected or double-space-terminated word, and failing an
  // irreversible-action confirmation on invisible whitespace reads as a bug.
  // Case is still exact, so autocapitalisation cannot confirm it for you.
  confirm: z
    .string({ error: `Type ${DELETE_CONFIRM_PHRASE} to confirm.` })
    .trim()
    .pipe(
      z.literal(DELETE_CONFIRM_PHRASE, {
        error: `Type ${DELETE_CONFIRM_PHRASE} to confirm.`,
      }),
    ),
});
